import { useState } from "react";

import { useGetClinicStatusForPatient } from "@/api/generated";
import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { XStack } from "@/components";
import { LinkText, Paragraph } from "@/components/Typography";
import SmsConsentDialog from "@/conversation/SmsConsentDialog";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import {
  useCurrentConversationId,
  useCurrentConversationType,
} from "@/hooks/conversation";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";

export default function SmsConsentBanner() {
  const isSignedIn = Boolean(useIsSignedInStable());
  const conversationId = useCurrentConversationId();
  const { data: conversationType } = useCurrentConversationType();
  const { data: userProfile } = useGetUserProfile({ enabled: isSignedIn });
  const { data: clinicStatus } = useGetClinicStatusForPatient({
    query: { enabled: isSignedIn },
  });
  const { mutate: updateConsents, isPending } = useUpdateConsents();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const [showConsentDialog, setShowConsentDialog] = useState(false);

  const consentStatus =
    userProfile?.consents?.[ConsentType.sms_consent_to_treat];
  const hasNeverBeenAsked =
    consentStatus === null || consentStatus === undefined;
  const hasClinicVisit = Boolean(clinicStatus?.latest_visit);

  if (
    !isSignedIn ||
    !userProfile ||
    !conversationId ||
    conversationType === "curai" ||
    !hasNeverBeenAsked ||
    !hasClinicVisit
  ) {
    return null;
  }

  const handleConsent = () => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.sms_consent_to_treat]: true,
          },
          source: "chat-banner",
        },
      },
      {
        onSuccess: () => {
          setShowConsentDialog(false);
        },
      },
    );
  };

  const handleDecline = () => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.sms_consent_to_treat]: false,
          },
          source: "chat-banner",
        },
      },
      {
        onSuccess: () => {
          setShowConsentDialog(false);
        },
      },
    );
  };

  return (
    <XStack
      backgroundColor="$navSelected"
      paddingVertical="$100"
      paddingHorizontal="$200"
      alignItems="center"
      gap="$100"
    >
      <Paragraph size="$sm">Did you know you can text us?</Paragraph>
      <LinkText
        size="$sm"
        bold
        onPress={() => {
          track("superclinical-__clicked-sms-consent-banner", {
            event_context: eventContext,
          });
          setShowConsentDialog(true);
        }}
      >
        Consent
      </LinkText>
      <SmsConsentDialog
        open={showConsentDialog}
        onOpenChange={setShowConsentDialog}
        onConsent={handleConsent}
        onDecline={handleDecline}
        isLoading={isPending}
      />
    </XStack>
  );
}
